export type CreatePeriodicIntervalParams = {
  run: () => Promise<void>
  baseIntervalMs?: number
  jitterRatio?: number
  maxBackoffMs?: number
}

export function createPeriodicInterval(params: CreatePeriodicIntervalParams): () => void {
  const baseIntervalMs = params.baseIntervalMs ?? 30_000
  const jitterRatio = params.jitterRatio ?? 0.2
  const maxBackoffMs = params.maxBackoffMs ?? 120_000

  let stopped = false
  let backoffMs = 0
  let timeout: number | undefined

  const withJitter = (ms: number) => {
    const delta = ms * jitterRatio
    const jitter = (Math.random() * 2 - 1) * delta
    return Math.max(1_000, Math.round(ms + jitter))
  }

  const scheduleNext = () => {
    if (stopped) return
    const waitMs = withJitter(Math.max(baseIntervalMs, backoffMs))
    timeout = window.setTimeout(() => void tick(), waitMs)
  }

  const tick = async () => {
    if (stopped) return
    try {
      await params.run()
      backoffMs = 0
    } catch {
      backoffMs = backoffMs === 0 ? 1_000 : Math.min(maxBackoffMs, backoffMs * 2)
    } finally {
      scheduleNext()
    }
  }

  const onOnline = () => {
    void tick()
  }
  const onVisibility = () => {
    if (document.visibilityState === 'visible') void tick()
  }

  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisibility)

  scheduleNext()

  return () => {
    stopped = true
    if (timeout) window.clearTimeout(timeout)
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisibility)
  }
}
